import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BadgeCheck,
  MapPinned,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  UserCheck,
  UserCog,
  UserX,
  X,
} from "lucide-react";
import { PatrolReportsPage } from "./PatrolReportsPage";
import { HospitalSelector } from "../../components/dashboard/HospitalSelector";
import { useStickyHospital } from "../../hooks/useStickyHospital";
import { useSort, SortHeader, CollapsibleCardHeader } from "../../components/ui/sortable";
import { cn } from "../../lib/utils";
import { useTranslation } from "../../i18n";
import { useAuthStore } from "../../state/auth";
import { fetchHospitals, type Hospital } from "../../services/hospitals";
import {
  fetchOfficers,
  createOfficer,
  updateOfficer,
  deactivateOfficer,
  approveOfficer,
  rejectOfficer,
  type Officer,
  type OfficerStatus,
} from "../../services/officers";

/**
 * Officer roster.
 *
 * An officer is not a dashboard user: they have no password and never sign in
 * here. They exist so a scan can be attributed to a person, and they identify
 * themselves on the handset with their Emory N number. That is why this page is
 * separate from User Management, which administers dashboard logins.
 *
 * Deactivating is a soft delete — the record is kept and set to INACTIVE, and
 * any open shift is closed. Scan history stays attributed, which matters: a
 * patrol record must not lose its officer because the account was later
 * disabled.
 */

const STATUS_STYLES: Record<OfficerStatus, string> = {
  ACTIVE: "bg-green-100 text-green-800 border-green-200",
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  INACTIVE: "bg-gray-100 text-gray-600 border-gray-200",
  SUSPENDED: "bg-red-100 text-red-800 border-red-200",
  REJECTED: "bg-red-100 text-red-700 border-red-200",
};

// PENDING is never assigned by hand — it only exists as the arrival state of a
// handset self-enrollment, and it leaves via Approve/Reject on the panel above
// the roster. Keeping it (and REJECTED) out of the manual status dropdown means
// the approval trail (approved_by / approved_at) can't be bypassed.
const STATUS_OPTIONS: OfficerStatus[] = ["ACTIVE", "INACTIVE", "SUSPENDED"];

export const OfficerManagementPage: React.FC = () => {
  const { t, lang } = useTranslation();
  const { user } = useAuthStore();

  const [hospitalId, setHospitalId] = useStickyHospital();
  const [statusFilter, setStatusFilter] = useState<string>("ACTIVE");
  const [search, setSearch] = useState("");
  const [officers, setOfficers] = useState<Officer[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Officer | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<Officer | null>(null);
  const [pending, setPending] = useState<Officer[]>([]);
  const [approving, setApproving] = useState<Officer | null>(null);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
    [lang]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pending self-enrollments are loaded regardless of the roster's status
      // filter — an approval request must never be hidden by a filter default.
      const [list, pendingList] = await Promise.all([
        fetchOfficers({ hospitalId, status: statusFilter }),
        fetchOfficers({ hospitalId, status: "PENDING" }),
      ]);
      setOfficers(list);
      setPending(pendingList);
      setError(null);
    } catch (err: any) {
      setError(err?.message || t("officers.loadFailed", "Could not load officers"));
    } finally {
      setLoading(false);
    }
  }, [hospitalId, statusFilter, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Hospital list for the create/edit form. Scoped client-side to the caller's
  // grants for the same defence-in-depth reason as HospitalSelector.
  useEffect(() => {
    if (!user?.email) return;
    void (async () => {
      try {
        const list = await fetchHospitals();
        const scoped =
          user.role !== "DIRECTOR" && user.hospital_ids && user.hospital_ids.length > 0
            ? list.filter((h) => user.hospital_ids!.includes(h.id))
            : list;
        setHospitals(scoped);
      } catch (err) {
        console.error("Failed to load hospitals", err);
      }
    })();
  }, [user?.email, user?.role, user?.hospital_ids]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return officers;
    return officers.filter(
      (o) =>
        o.name.toLowerCase().includes(term) ||
        o.n_number.toLowerCase().includes(term) ||
        (o.email ?? "").toLowerCase().includes(term)
    );
  }, [officers, search]);

  // Collapsible + click-to-sort roster, matching the Recorded Activity table.
  const [rosterOpen, setRosterOpen] = useState(true);
  const rosterSort = useSort(visible, {
    officer: (o) => o.name,
    n_number: (o) => o.n_number,
    site: (o) => o.hospital_name,
    status: (o) => o.status,
    shift: (o) => o.session_started ?? null,
  });

  const onSaved = async () => {
    setShowForm(false);
    setEditing(null);
    await load();
  };

  const doDeactivate = async () => {
    if (!confirmDeactivate) return;
    setBusy(true);
    try {
      await deactivateOfficer(confirmDeactivate.id);
      setConfirmDeactivate(null);
      setError(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("officers.deactivateFailed", "Could not deactivate officer"));
    } finally {
      setBusy(false);
    }
  };

  const doReject = async (o: Officer) => {
    setBusy(true);
    try {
      await rejectOfficer(o.id);
      setError(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("officers.rejectFailed", "Could not reject enrollment"));
    } finally {
      setBusy(false);
    }
  };

  const onShiftCount = officers.filter((o) => o.active_session_id).length;

  return (
    <div className="space-y-4 p-4 md:space-y-6 md:p-6">
      {/* Patrol Reports first (consolidated from the retired Patrol Reports
          tab — its route redirects here), then the roster below. */}
      <PatrolReportsPage embedded />

      <div className="flex flex-col gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800 md:text-2xl">
            {t("officers.title", "Officers")}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {t(
              "officers.subtitle",
              "Officers who sign on with an N number so their scans are attributed. These are not dashboard logins."
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HospitalSelector selectedHospitalId={hospitalId} onHospitalChange={setHospitalId} />
          <button
            onClick={() => void load()}
            className="flex min-h-[44px] items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            {t("common.refresh", "Refresh")}
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            {t("officers.add", "Add officer")}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label={t("common.close", "Close")}>
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("officers.searchPlaceholder", "Search by name, N number or email")}
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="ACTIVE">{t("officers.filterActive", "Active")}</option>
          <option value="INACTIVE">{t("officers.filterInactive", "Inactive")}</option>
          <option value="SUSPENDED">{t("officers.filterSuspended", "Suspended")}</option>
          <option value="PENDING">{t("officers.filterPending", "Pending approval")}</option>
          <option value="REJECTED">{t("officers.filterRejected", "Rejected")}</option>
          <option value="ALL">{t("officers.filterAll", "All statuses")}</option>
        </select>
      </div>

      {onShiftCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
          <BadgeCheck className="h-4 w-4 flex-shrink-0" />
          {t("officers.onShiftCount", "Currently signed on:")} <strong>{onShiftCount}</strong>
        </div>
      )}

      {/* Pending self-enrollments — approval requests routed here by the
          enrollment email. Shown above the roster so they cannot be missed. */}
      {pending.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50/60 shadow-sm">
          <div className="flex items-center gap-2 border-b border-amber-200 px-4 py-3">
            <UserCheck className="h-4 w-4 flex-shrink-0 text-amber-700" />
            <h2 className="text-sm font-bold text-amber-900">
              {t("officers.pendingTitle", "Pending enrollment approvals")} ({pending.length})
            </h2>
          </div>
          <div className="divide-y divide-amber-100">
            {pending.map((o) => (
              <div key={o.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => navigate(`/supervisor/officers/${o.id}`)}
                      className="font-medium text-[#3B7BBE] hover:underline"
                      title={t("officers.viewProfile", "View officer profile")}
                    >
                      {o.name}
                    </button>
                    <span className="font-mono text-xs text-gray-600">{o.n_number}</span>
                    <span className="rounded-sm border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                      {t("officers.selfEnrolled", "Self-enrolled")}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    {o.hospital_name || "—"}
                    {o.enroll_at
                      ? ` · ${t("officers.enrolledAt", "signed on")} ${dateFormat.format(new Date(o.enroll_at))}`
                      : ""}
                    {typeof o.enroll_distance_m === "number" ? (
                      <span className={cn(o.enroll_distance_m > 1000 && "font-semibold text-red-700")}>
                        {" · "}
                        {t("officers.enrollDistance", "{d} from the site's recorded scan area").replace(
                          "{d}",
                          o.enroll_distance_m >= 1000
                            ? `${(o.enroll_distance_m / 1000).toFixed(1)} km`
                            : `${o.enroll_distance_m} m`
                        )}
                      </span>
                    ) : (
                      <span> · {t("officers.enrollNoGps", "no GPS fix at sign-on")}</span>
                    )}
                  </div>
                  {o.enroll_gps_hospital_id && o.enroll_gps_hospital_id !== o.hospital_id && (
                    <div className="mt-1 text-xs font-semibold text-red-700">
                      {t(
                        "officers.enrollSiteMismatch",
                        "Selected {chosen}, but GPS pointed to {gps} — confirm which is right before approving."
                      )
                        .replace("{chosen}", o.hospital_name || "—")
                        .replace("{gps}", o.enroll_gps_hospital_name || "another site")}
                    </div>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <button
                    onClick={() => setApproving(o)}
                    disabled={busy}
                    className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {t("officers.approve", "Approve")}
                  </button>
                  <button
                    onClick={() => void doReject(o)}
                    disabled={busy}
                    className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    {t("officers.reject", "Reject")}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-amber-100 px-4 py-2 text-[11px] text-amber-800/80">
            {t(
              "officers.pendingNote",
              "These identities were self-reported at a badge scan. Their scans attribute provisionally until approved; rejecting keeps the record and its scans for review."
            )}
          </div>
        </div>
      )}

      {/* Roster */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <CollapsibleCardHeader
          title={t("officers.rosterTitle", "Officer Roster")}
          count={visible.length}
          open={rosterOpen}
          onToggle={() => setRosterOpen((o) => !o)}
          collapseLabel={t("common.collapse", "Collapse")}
          expandLabel={t("common.expand", "Expand")}
        />
        {rosterOpen && (loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <UserCog className="mb-4 h-12 w-12 text-gray-300" />
            <p className="text-gray-500">
              {search
                ? t("officers.emptySearch", "No officers match that search.")
                : t("officers.empty", "No officers yet. Add one to start attributing scans.")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <SortHeader label={t("officers.colOfficer", "Officer")} k="officer" sort={rosterSort.sort} onToggle={rosterSort.toggle} />
                  <SortHeader label={t("officers.colNNumber", "N number")} k="n_number" sort={rosterSort.sort} onToggle={rosterSort.toggle} />
                  <SortHeader label={t("officers.colSite", "Site")} k="site" sort={rosterSort.sort} onToggle={rosterSort.toggle} />
                  <SortHeader label={t("officers.colStatus", "Status")} k="status" sort={rosterSort.sort} onToggle={rosterSort.toggle} />
                  <SortHeader label={t("officers.colShift", "Current shift")} k="shift" sort={rosterSort.sort} onToggle={rosterSort.toggle} />
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("officers.colActions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rosterSort.sorted.map((o) => (
                  <tr key={o.id} className="transition-colors hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => navigate(`/supervisor/officers/${o.id}`)}
                        className="text-left font-medium text-[#3B7BBE] hover:underline"
                        title={t("officers.viewProfile", "View officer profile")}
                      >
                        {o.name}
                      </button>
                      <div className="text-xs text-gray-500">{o.email || o.phone || "—"}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 font-mono text-sm text-gray-700">
                      {o.n_number}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{o.hospital_name || "—"}</td>
                    <td className="px-6 py-4">
                      <span
                        className={cn(
                          "inline-block whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium",
                          STATUS_STYLES[o.status]
                        )}
                      >
                        {t(`officers.status.${o.status}`, o.status)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      {o.active_session_id ? (
                        <span className="text-green-700">
                          {t("officers.onShiftSince", "On since")}{" "}
                          {o.session_started ? dateFormat.format(new Date(o.session_started)) : "—"}
                          <span className="text-gray-500">
                            {" "}
                            · {o.session_scans ?? 0} {t("officers.scans", "scans")}
                          </span>
                        </span>
                      ) : (
                        <span className="text-gray-400">{t("officers.offShift", "Not signed on")}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <button
                        onClick={() =>
                          navigate(`/supervisor/scan-map?officerId=${o.id}&officerName=${encodeURIComponent(o.name)}`)
                        }
                        className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-blue-50 hover:text-blue-600"
                        aria-label={t("officerMap.open", "View scan history on the Scan Map")}
                        title={t("officerMap.open", "View scan history on the Scan Map")}
                      >
                        <MapPinned className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditing(o);
                          setShowForm(true);
                        }}
                        className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-blue-50 hover:text-blue-600"
                        aria-label={t("common.edit", "Edit")}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {o.status !== "INACTIVE" && (
                        <button
                          onClick={() => setConfirmDeactivate(o)}
                          className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600"
                          aria-label={t("officers.deactivate", "Deactivate")}
                        >
                          <UserX className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {showForm && (
        <OfficerForm
          officer={editing}
          hospitals={hospitals}
          defaultHospitalId={hospitalId}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSaved={onSaved}
        />
      )}

      {approving && (
        <ApproveDialog
          officer={approving}
          onClose={() => setApproving(null)}
          onApprove={async (name) => {
            // Throws on failure — ApproveDialog shows the message inline and
            // stays open, so an approval error is never hidden behind the modal.
            await approveOfficer(approving.id, name);
            setApproving(null);
            setError(null);
            await load();
          }}
        />
      )}

      {confirmDeactivate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="max-w-sm rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-3 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 flex-shrink-0 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-800">
                {t("officers.deactivateTitle", "Deactivate officer?")}
              </h3>
            </div>
            <p className="text-sm text-gray-600">
              {t(
                "officers.deactivateBody",
                "{name} will no longer be able to sign on, and any open shift is closed. Their scan history is kept and stays attributed to them."
              ).replace("{name}", confirmDeactivate.name)}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setConfirmDeactivate(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                onClick={() => void doDeactivate()}
                disabled={busy}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {t("officers.deactivate", "Deactivate")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────── */

interface OfficerFormProps {
  officer: Officer | null;
  hospitals: Hospital[];
  defaultHospitalId: string | undefined;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const OfficerForm: React.FC<OfficerFormProps> = ({
  officer,
  hospitals,
  defaultHospitalId,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const isEdit = officer !== null;

  const [form, setForm] = useState({
    n_number: officer?.n_number ?? "",
    name: officer?.name ?? "",
    email: officer?.email ?? "",
    phone: officer?.phone ?? "",
    hospital_id: officer?.hospital_id ?? defaultHospitalId ?? hospitals[0]?.id ?? "",
    status: (officer?.status ?? "ACTIVE") as OfficerStatus,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.n_number.trim() || !form.name.trim() || !form.hospital_id) {
      setError(t("officers.formRequired", "N number, name and site are required"));
      return;
    }

    setSaving(true);
    try {
      if (isEdit && officer) {
        await updateOfficer(officer.id, {
          n_number: form.n_number.trim(),
          name: form.name.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          hospital_id: form.hospital_id,
          status: form.status,
        });
      } else {
        await createOfficer({
          n_number: form.n_number.trim(),
          name: form.name.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          hospital_id: form.hospital_id,
        });
      }
      await onSaved();
    } catch (err: any) {
      setError(err?.message || t("officers.saveFailed", "Could not save officer"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <form onSubmit={submit} className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEdit ? t("officers.editTitle", "Edit officer") : t("officers.addTitle", "Add officer")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100"
            aria-label={t("common.close", "Close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="officer-n-number">
              {t("officers.fieldNNumber", "N number")}
            </label>
            <input
              id="officer-n-number"
              value={form.n_number}
              onChange={change("n_number")}
              placeholder="N123456 or CSACJO"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t("officers.fieldLoginIdHint2", "Enter the ID exactly as it appears on the badge (e.g. N123456 or CSACJO). It is saved exactly as typed.")}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="officer-name">
              {t("officers.fieldName", "Full name")}
            </label>
            <input
              id="officer-name"
              value={form.name}
              onChange={change("name")}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="officer-site">
              {t("officers.fieldSite", "Site")}
            </label>
            <select
              id="officer-site"
              value={form.hospital_id}
              onChange={change("hospital_id")}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">{t("officers.selectSite", "Select a site")}</option>
              {hospitals.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="officer-email">
                {t("officers.fieldEmail", "Email")}
              </label>
              <input
                id="officer-email"
                type="email"
                value={form.email ?? ""}
                onChange={change("email")}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="officer-phone">
                {t("officers.fieldPhone", "Phone")}
              </label>
              <input
                id="officer-phone"
                value={form.phone ?? ""}
                onChange={change("phone")}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="officer-status">
                {t("officers.fieldStatus", "Status")}
              </label>
              <select
                id="officer-status"
                value={form.status}
                onChange={change("status")}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`officers.status.${s}`, s)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {t(
                  "officers.fieldStatusHint",
                  "Only ACTIVE officers can sign on. Changing away from ACTIVE closes any open shift."
                )}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────── */

/**
 * Approval confirms an identity, so the name is shown editable — the officer
 * typed it themselves on a handset, and this is the supervisor's chance to fix
 * casing, typos, or a nickname before it becomes the attributed record.
 */
const ApproveDialog: React.FC<{
  officer: Officer;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onApprove: (name: string) => void | Promise<void>;
}> = ({ officer, onClose, onApprove }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(officer.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onApprove(name.trim());
      // On success the parent unmounts this dialog; nothing more to do.
    } catch (e: any) {
      setErr(e?.message || t("officers.approveFailed", "Could not approve enrollment"));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-gray-800">
          {t("officers.approveTitle", "Approve enrollment?")}
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          {t(
            "officers.approveBody",
            "Confirms {n} as an officer at {site}. Their pending scans stay attributed and future sign-ons work as normal."
          )
            .replace("{n}", officer.n_number)
            .replace("{site}", officer.hospital_name || "this site")}
        </p>
        <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="approve-name">
          {t("officers.approveName", "Officer name (as it should appear)")}
        </label>
        <input
          id="approve-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        {err && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {err}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            onClick={() => void submit()}
            disabled={busy || !name.trim()}
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
          >
            {busy ? t("common.saving", "Saving…") : t("officers.approve", "Approve")}
          </button>
        </div>
      </div>
    </div>
  );
};
